import { AfterViewInit, ChangeDetectionStrategy, Component, inject } from '@angular/core';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import {
  ModalController,
  ViewWillEnter,
  ViewWillLeave,
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonRow,
} from '@ionic/angular/standalone';
import { LogLifecycle } from '@decorators/log-lifecycle.decorator';
import { TranslateModule } from '@ngx-translate/core';
import { LocalStorageService } from '@services/local-storage.service';
import { NgOptimizedImage } from '@angular/common';
import { StatusBarInfo } from '@capacitor/status-bar';
import { StatusBarService } from '@services/status-bar.service';
import { PlatformService } from '@services/platform.service';
import { environment } from '@environments/environment';


@Component({
  selector: 'tutorial-control',
  templateUrl: './tutorial-control.component.html',
  styleUrls: ['./tutorial-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    IonButton,
    IonCol,
    IonContent,
    IonGrid,
    IonRow,
    TranslateModule,
    NgOptimizedImage,
  ],
})
@LogLifecycle()
export class TutorialControlComponent implements AfterViewInit, ViewWillEnter, ViewWillLeave {
  private readonly platformService = inject(PlatformService);
  private readonly modalController = inject(ModalController);
  private readonly localStorage = inject(LocalStorageService);
  private readonly statusBarService = inject(StatusBarService);

  private wasOverlays: boolean = false;
  private swiper: Swiper;
  private totalSlides: number = 3;

  async ionViewWillEnter(): Promise<void> {
    if (environment.useStatusBarOverlays && this.platformService.isAndroidApp) {
      this.statusBarService.getInfo().then(async (info: StatusBarInfo) => {
        this.wasOverlays = info.overlays;

        if (this.wasOverlays) {
          await this.statusBarService.deactivateOverlays();
        }
      });
    }
  }

  async ionViewWillLeave(): Promise<void> {
    if (environment.useStatusBarOverlays && this.platformService.isAndroidApp && this.wasOverlays) {
      await this.statusBarService.activateOverlays();
    }
  }

  ngAfterViewInit() {
    this.initializeSwiper();
  }

  public toNextSlide(): void {
    this.swiper?.slideNext(500);
  }

  public async finishLearning(): Promise<void> {
    this.markTutorialAsPassed();
    await this.modalController?.dismiss();
  }

  private initializeSwiper(): void {
    if (typeof document === 'undefined') return;

    this.swiper = new Swiper(
      '.tutorial-swiper',
      {
        modules: [Pagination],
        loop: false,
        initialSlide: 0,
        pagination: {
          el: '.swiper-pagination.tutorial-pagination',
          clickable: true,
          dynamicBullets: false,
        },
        on: {
          touchStart: (swiper: Swiper): void => {
            if (swiper?.wrapperEl) {
              swiper.wrapperEl.setAttribute('current', swiper.activeIndex.toString());
            }
          },
          transitionStart: async (swiper: Swiper): Promise<void> => {
            const currentIndex: number = parseInt(swiper.wrapperEl.getAttribute('current'), 10);

            if (currentIndex == this.totalSlides - 1 && swiper.activeIndex == currentIndex) {
              await this.finishLearning();
            }
          },
        },
      }
    );
  }

  public async closeTutorial(): Promise<void> {
    this.markTutorialAsPassed();
    await this.modalController?.dismiss();
  }

  private markTutorialAsPassed(): void {
    this.localStorage.setItem('tutorialControlPassed', true);
  }
}
